import { useState, useEffect, useMemo, useContext } from "react";
import StaffNav from "./managernav";
import { useNavigate, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { AuthContext } from "../AuthContext";




// Check if the app is running in an Electron environment

function ScoreCircle({ total, max }) {
    const percent = Math.min((total / max) * 100, 100);
    const radius = 60;
    const stroke = 10;
    const normalizedRadius = radius - stroke / 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = circumference - (percent / 100) * circumference;

    return (
        <div className="flex flex-col items-center mb-8">
            <svg height={radius * 2} width={radius * 2}>
                <circle
                    stroke="#e5e7eb"
                    fill="transparent"
                    strokeWidth={stroke}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                />
                <circle
                    stroke="#4f46e5"
                    fill="transparent"
                    strokeWidth={stroke}
                    strokeLinecap="round"
                    strokeDasharray={circumference + " " + circumference}
                    style={{ strokeDashoffset, transition: "stroke-dashoffset 0.5s" }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                />
                <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dy=".3em"
                    fontSize="1.5rem"
                    fill="#111827"
                    fontWeight="bold"
                >
                    {total} / {max}
                </text>
            </svg>
            <span className="text-gray-500 mt-2 text-sm font-medium">Total Score</span>
        </div>
    );
}

export default function InterviewMeeting() {
    const navigate = useNavigate();
    const location = useLocation();
    const meetingId = location.state?.meeting.id;

    console.log('InterviewMeeting meetingId: ' + meetingId);

    const [meeting, setMeeting] = useState(null);
    const [interview, setInterview] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [answers, setAnswers] = useState({});
    const [scores, setScores] = useState({});
    const [start, setStart] = useState(false);
    const [time, setTime] = useState(0);
    const [resume, setResume] = useState(null);
    const [expandResume, setExpandResume] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showCompletePrompt, setShowCompletePrompt] = useState(false);

    const user = useContext(AuthContext);

    useEffect(() => {
        if (!showCompletePrompt) return;
        const timeout = setTimeout(() => {
            navigate('/recruitment');
        }, 1500);
        return () => clearTimeout(timeout);
    }, [showCompletePrompt, navigate]);

    // Fetch interview meeting data
    useEffect(() => {
        const fetchInterviewMeeting = async () => {
            const response = await fetch(
                `https://api.insightos.co.uk/api/interviewmeeting/${meetingId}`,
                {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                }
            );
            const data = await response.json();
            setMeeting(data.data.meeting);
            setInterview(data.data.interview);
            setQuestions(data.data.questions || []);
            setResume(data.data.resume);

            console.log(data.data.resume);
        };
        if (meetingId) fetchInterviewMeeting();
    }, [meetingId]);

    // Launch Notetaker
    // useEffect(() => {
    //     if (meeting && meeting.id) {
    //         const url = `http://localhost:3000/notetaker/${meeting.id}`;
    //         if (isElectron) {
    //             try {
    //                 const { ipcRenderer } = window.require("electron");
    //                 ipcRenderer.send("open-external-url", { url, browserName: "chrome" });
    //             } catch (err) {
    //                 // ignore
    //             }
    //         } else {
    //             window.open(url, "_blank");
    //         }
    //     }
    // }, [meeting]);

    // Timer
    useEffect(() => {
        if (start) {
            const interval = setInterval(() => setTime((prev) => prev + 1), 1000);
            return () => clearInterval(interval);
        }
    }, [start]);
    const formattedTime = useMemo(() => {
        const hours = String(Math.floor(time / 3600)).padStart(2, '0');
        const minutes = String(Math.floor((time % 3600) / 60)).padStart(2, '0');
        const seconds = String(time % 60).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    }, [time]);

    // Handlers
    const handleAnswerChange = (id, value) => {
        setAnswers((prev) => ({ ...prev, [id]: value }));
    };
    const handleScoreChange = (id, value) => {
        let val = Math.max(0, Math.min(10, Number(value)));
        setScores((prev) => ({ ...prev, [id]: val }));
    };

    const handleStartMeeting = async () => {
        setTime(0);
        try {
            await fetch(`https://api.insightos.co.uk/api/startmeeting`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ id: meetingId, Attended: user?.name }),
            });
        } catch (err) {
            // Optionally handle error
        }
        setStart(true);

        // Open Notetaker in a new window
        const url = `https://app.insightos.co.uk/notetaker/${meetingId}`;
        if (window?.electronAPI) {
            // If using Electron preload script
            window.electronAPI.openExternalUrl(url, "chrome");
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };


    const handleSubmit = async () => {
        setSubmitting(true);
        const payload = questions.map((q) => ({
            id: q.id,
            Answer: answers[q.id] ?? "",
            Score: typeof scores[q.id] === "number" ? scores[q.id] : null,
        }));
        const totalScoreValue = payload.reduce((sum, resp) => sum + (Number(resp.Score) || 0), 0);
        try {
            const response = await fetch("https://api.insightos.co.uk/api/interviewanswers", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ meetingId, responses: payload, totalScore: totalScoreValue }),
            });

            if (!response.ok) {
                throw new Error('Failed to submit interview answers.');
            }

            setShowCompletePrompt(true);
        } catch (error) {
            alert(error?.message || 'Failed to submit interview answers.');
        } finally {
            setSubmitting(false);
        }
    };

    // Calculate total score
    const totalScore = useMemo(
        () =>
            questions.reduce(
                (sum, q) => sum + (Number(scores[q.id]) || 0),
                0
            ),
        [scores, questions]
    );
    const maxScore = questions.length * 10;

    return (
        <>
            <StaffNav />
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-100 via-sky-50 to-emerald-50 p-4">
                <div className="w-full max-w-4xl min-h-screen p-8 border border-indigo-200/60 rounded-3xl bg-white/90 shadow-[0_20px_60px_rgba(79,70,229,0.18)] overflow-auto backdrop-blur-xl">
                    <ScoreCircle total={totalScore} max={maxScore || 100} />
                    <div className="mb-8 grid grid-cols-1 md:grid-cols-2 gap-6 rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-50/90 via-white/90 to-sky-50/90 p-5 shadow-sm">
                        <div>
                            <h2 className="text-2xl font-bold mb-2 text-indigo-700">Interview Meeting</h2>
                            {meeting && (
                                <div className="space-y-1 text-gray-700">
                                    <div>
                                        <span className="font-semibold">Name:</span> {meeting.Name}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Date:</span>{" "}
                                        {meeting.Date ? format(new Date(meeting.Date), "MMM dd, yyyy") : "N/A"}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Time:</span> {meeting.Time}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Notes:</span> {meeting.Notes}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            {interview && (
                                <div className="space-y-1 text-gray-700">
                                    <div>
                                        <span className="font-semibold">Interviewee:</span> {interview.Interviewee}
                                    </div>
                                    <div>
                                        <span className="font-semibold">Role:</span> {interview.Role}
                                    </div>
                                </div>
                            )}
                            <div className="mt-4 flex gap-3">
                                {!start ? (
                                    <button
                                        onClick={handleStartMeeting}
                                        className="rounded-full bg-indigo-600 px-6 py-2 text-white font-semibold shadow-md hover:bg-indigo-700 transition"
                                    >
                                        Start Meeting
                                    </button>
                                ) : (
                                    <span className="inline-block text-sm text-gray-500 bg-indigo-50 px-3 py-1 rounded-full">
                                        Time Elapsed: {formattedTime}
                                    </span>
                                )}
                                {resume && (
                                    <button
                                        onClick={() => setExpandResume(!expandResume)}
                                        className="rounded-full bg-purple-600 px-6 py-2 text-white font-semibold shadow-md hover:bg-purple-700 transition"
                                    >
                                        {expandResume ? '📄 Hide Resume' : '📄 View Resume'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Resume Expansion Section */}
                    {expandResume && resume && (
                        <div className="mb-8">
                            <div className="max-w-full mx-auto">
                                {/* Mock CV Layout */}
                                <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 border border-gray-200">
                                    {/* CV Header */}
                                    <div className="border-b-2 border-indigo-500 pb-4 mb-6">
                                        <h3 className="text-2xl md:text-3xl font-bold text-gray-900">{resume.CandidateName || 'Candidate Name'}</h3>
                                        <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
                                            <span className="flex items-center gap-1">📧 {resume.Email || 'N/A'}</span>
                                            <span className="flex items-center gap-1">📱 {resume.Telephone || 'N/A'}</span>
                                            <span className="flex items-center gap-1">📍 {resume.Location || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {/* Left Sidebar - Qualifications */}
                                        <div className="md:col-span-1 space-y-4">
                                            <div className="bg-indigo-50 rounded-lg p-4">
                                                <h4 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                                                    🎓 Qualifications
                                                </h4>
                                                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                    {resume.Qualifications || 'No qualifications provided'}
                                                </div>
                                            </div>

                                            <div className="bg-green-50 rounded-lg p-4">
                                                <h4 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
                                                    ⭐ AI Score
                                                </h4>
                                                <div className="text-center">
                                                    <div className={`text-3xl font-bold ${
                                                        resume.Score >= 75 ? 'text-green-600' :
                                                        resume.Score >= 50 ? 'text-yellow-600' :
                                                        'text-red-600'
                                                    }`}>
                                                        {resume.Score}/100
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-blue-50 rounded-lg p-4">
                                                <h4 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                                    💬 AI Feedback
                                                </h4>
                                                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                    {resume.Feedback || 'No feedback provided'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Main Content - About & Experience */}
                                        <div className="md:col-span-2 space-y-6">
                                            <div>
                                                <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2 border-b-2 border-gray-200 pb-2">
                                                    👤 About
                                                </h4>
                                                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                    {resume.About || 'No information provided'}
                                                </div>
                                            </div>

                                            <div>
                                                <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2 border-b-2 border-gray-200 pb-2">
                                                    💼 Experience
                                                </h4>
                                                <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                    {resume.Experience || 'No experience provided'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSubmit();
                        }}
                        className="mt-8"
                    >
                        <h3 className="text-lg font-semibold mb-4 text-indigo-700">Interview Questions</h3>
                        <div className="space-y-8">
                            {questions.map((q, idx) => (
                                <div
                                    key={q.id}
                                    className="rounded-xl border border-gray-200 p-6 shadow-sm bg-gray-50"
                                >
                                    <div className="mb-3 font-medium text-gray-900 flex items-center gap-2">
                                        <span className="inline-block bg-indigo-100 text-indigo-700 rounded-full px-3 py-1 text-xs font-bold">
                                            Q{idx + 1}
                                        </span>
                                        <span>{q.Question}</span>
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-4">
                                        <textarea
                                            rows={3}
                                            placeholder="Type answer..."
                                            className="border rounded-lg px-3 py-2 flex-1 focus:ring-2 focus:ring-indigo-200 outline-none"
                                            value={answers[q.id] || ""}
                                            onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                                            required
                                        />
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                placeholder="Score"
                                                className="border rounded-lg px-3 py-2 w-24 focus:ring-2 focus:ring-indigo-200 outline-none"
                                                value={scores[q.id] ?? ""}
                                                onChange={(e) => handleScoreChange(q.id, e.target.value)}
                                                min={0}
                                                max={10}
                                                required
                                            />
                                            <span className="text-gray-500 text-sm">/ 10</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="submit"
                            data-component="interview-meeting-form"
                            data-feature="submit_interview_post"
                            data-time-saved="30"
                            disabled={submitting}
                            className="mt-10 rounded-full bg-gradient-to-r from-emerald-600 to-sky-600 px-8 py-3 text-white font-semibold shadow-lg hover:from-emerald-500 hover:to-sky-500 transition text-lg disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Submitting...' : 'Submit Interview'}
                        </button>
                    </form>
                </div>
            </div>

            {showCompletePrompt && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-indigo-950/40 p-4">
                    <div className="w-full max-w-md rounded-2xl border border-indigo-200/70 bg-gradient-to-r from-indigo-100/95 via-white/95 to-sky-100/95 p-6 shadow-2xl">
                        <h3 className="text-lg font-bold text-indigo-900">Interview Complete</h3>
                        <p className="mt-2 text-sm text-indigo-800">
                            Interview answers have been submitted successfully. Redirecting to Recruitment...
                        </p>
                        <div className="mt-4 flex justify-end">
                            <button
                                type="button"
                                onClick={() => navigate('/recruitment')}
                                className="rounded-lg bg-gradient-to-r from-indigo-600 to-sky-600 px-4 py-2 text-sm font-semibold text-white hover:from-indigo-500 hover:to-sky-500"
                            >
                                Go now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
